/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * UpdateApplicationUserPage
 *
 * - Fetches existing user details using:
 *      GET /arcee/user/<user_id>/details/
 * - Updates user using:
 *      PATCH /arcee/user/<user_id>/update/
 * - Uses UpdateApplicationUserComponent for form UI
 * - Shows ProgressBarDialog during network calls
 * - Maps server validation errors into form
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import UpdateApplicationUserComponent from '../components/applications/UpdateApplicationUserComponent';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export default function UpdateApplicationUserPage() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { id, userId } = useParams(); // id = application id, userId = application user id

    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [loadingUser, setLoadingUser] = useState(false);

    const fetchControllerRef = useRef(null);
    const updateControllerRef = useRef(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('info');

    const [serverErrors, setServerErrors] = useState(null);
    const [initialValues, setInitialValues] = useState(null);

    // Map DRF error payload into component fields
    function mapServerErrors(errBody) {
        if (!errBody || typeof errBody !== 'object') return null;
        const mapped = {};
        const keyMap = {
            'first_name': 'firstName',
            'last_name': 'lastName',
            'username': 'username',
            'mail_credentials': 'sendMail',
            'password': 'password',
        };

        for (const [k, v] of Object.entries(errBody)) {
            const targetKey = keyMap[k] || k;
            if (Array.isArray(v)) mapped[targetKey] = v.join(' ');
            else mapped[targetKey] = String(v);
        }
        return mapped;
    }

    // Fetch existing user details on mount
    useEffect(() => {
        if (!userId) {
            setAlertTitle('Missing User');
            setAlertMessage('User id is missing. Please access this page from the user list.');
            setAlertType('error');
            setAlertOpen(true);
            return;
        }

        const controller = new AbortController();
        fetchControllerRef.current = controller;

        async function fetchUser() {
            setLoadingUser(true);
            setProgressMessage('Loading user details...');
            setProgressOpen(true);

            try {
                const resp = await apiHelper.apiCall(`/arcee/user/${userId}/details/?application_id=${id}`, {
                    method: 'GET',
                    signal: controller.signal,
                });

                if (!resp.ok) {
                    let errBody;
                    try {
                        errBody = await resp.json();
                    } catch {
                        try {
                            errBody = await resp.text();
                        } catch {
                            errBody = null;
                        }
                    }

                    const message =
                        errBody && typeof errBody === 'object' && errBody.error
                            ? errBody.error
                            : typeof errBody === 'string'
                                ? errBody
                                : `Failed to load user details (HTTP ${resp.status}).`;

                    setAlertTitle('Error');
                    setAlertMessage(message);
                    setAlertType('error');
                    setAlertOpen(true);
                    return;
                }

                const data = await resp.json();

                setInitialValues({
                    username: data.username || '',
                    email: data.email || '',
                    firstName: data.first_name || '',
                    lastName: data.last_name || '',
                    sendMail: false, // default; user can opt in during update
                });
            } catch (err) {
                if (err && err.name === 'AbortError') return;
                console.error('Fetch user error:', err);
                setAlertTitle('Network Error');
                setAlertMessage(err.message || 'Could not contact server. Please try again later.');
                setAlertType('error');
                setAlertOpen(true);
            } finally {
                setLoadingUser(false);
                setProgressOpen(false);
                fetchControllerRef.current = null;
            }
        }

        fetchUser();

        return () => {
            if (fetchControllerRef.current) {
                fetchControllerRef.current.abort();
                fetchControllerRef.current = null;
            }
        };
    }, [userId]);

    // Submit handler (PATCH update)
    const handleSubmit = async (payload) => {
        if (!userId) {
            setAlertTitle('Missing User');
            setAlertMessage('User id is missing. Please access this page from the user list.');
            setAlertType('error');
            setAlertOpen(true);
            return;
        }

        if (submitting) return;
        setSubmitting(true);
        setProgressMessage('Updating user...');
        setProgressOpen(true);
        setServerErrors(null);

        const controller = new AbortController();
        updateControllerRef.current = controller;

        const bodyPayload = {
            first_name: payload.firstName || '',
            last_name: payload.lastName || '',
            mail_credentials: !!payload.sendMail,
            application_id: id,
        };

        if (payload.username) {
            bodyPayload.username = payload.username;
        }

        if (payload.password) {
            bodyPayload.password = payload.password;
        }

        try {
            const resp = await apiHelper.apiCall(`/arcee/user/${userId}/update/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyPayload),
                signal: controller.signal,
            });

            if (!resp.ok) {
                let errBody;
                try {
                    errBody = await resp.json();
                } catch {
                    try {
                        errBody = await resp.text();
                    } catch {
                        errBody = null;
                    }
                }

                console.warn('Update user failed', { status: resp.status, body: errBody });

                if (errBody && typeof errBody === 'object' && !Array.isArray(errBody)) {
                    const mapped = mapServerErrors(errBody);
                    setServerErrors(mapped);
                    return;
                }

                const message =
                    typeof errBody === 'string' ? errBody : `HTTP ${resp.status}`;
                setAlertTitle('Failed to update user');
                setAlertMessage(message);
                setAlertType('error');
                setAlertOpen(true);
                return;
            }

            const data = await resp.json().catch(() => ({}));
            setServerErrors(null);
            const successMsg = data.message || 'User updated successfully.';
            setAlertTitle('Success');
            setAlertMessage(successMsg);
            setAlertType('success');
            setAlertOpen(true);
        } catch (err) {
            if (err && err.name === 'AbortError') return;
            console.error('Update user error:', err);
            setAlertTitle('Network Error');
            setAlertMessage(err.message || 'Could not contact server. Please try again later.');
            setAlertType('error');
            setAlertOpen(true);
        } finally {
            setProgressOpen(false);
            setSubmitting(false);
            updateControllerRef.current = null;
        }
    };

    const handleCancel = () => {
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
            fetchControllerRef.current = null;
        }
        if (updateControllerRef.current) {
            updateControllerRef.current.abort();
            updateControllerRef.current = null;
        }

        if (id) {
            navigate(`/dashboard/applications/${id}/users`);
        } else {
            navigate('/dashboard/applications');
        }
        window.location.reload();
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        if (alertType === 'success') {
            if (id) {
                navigate(`/dashboard/applications/${id}/users`);
            } else {
                navigate('/dashboard/applications');
            }
            window.location.reload();
        }
    };

    return (
        <>
            <UpdateApplicationUserComponent
                initialValues={initialValues}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                serverErrors={serverErrors}
            />

            <ProgressBarDialog
                open={progressOpen}
                text_message={progressMessage}
                cancellable={false}
            />

            <AlertDialog
                open={alertOpen}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                okLabel="OK"
                showCancel={false}
                onOk={handleAlertClose}
                onCancel={() => setAlertOpen(false)}
                mini_mode={false}
            />
        </>
    );
}
